import {
  NavigationsObject,
  RouteDefinition,
  Routes,
  RoutesDefinitions,
} from 'embeddables/src/Route'
import { EmbedMessageSettings } from 'embeddables/src/types/Settings'
import { injectUrlWithParams } from 'pejer/src/helpers'
import { reactive, Ref, ref, toRaw } from 'vue'
import { RouteLocationNormalized, RouteLocationNormalizedLoaded, Router } from 'vue-router'
import {
  NAVIGATION_REASON,
  NAVIGATION_REASON_EVENT,
  NAVIGATION_REASON_INDIRECT,
  NAVIGATION_REASON_ROUTER,
} from '../Events'
import { PejerEmbeddables } from '../PejerEmbeddables'
import { fillRouteWithDefaults, getDefaultTableRoutesOptions } from '../defaults'

export const HISTORY_STATE_ROUTE_OBJECT = 'routeObject'

export class RouteHandler {
  pejer: PejerEmbeddables
  router: Router

  /**
   * These are the settings for the routes that are displayable,
   * these are a one-to-one mapping of the routes that are displayed
   */
  settings = reactive<{
    [key: string]: RoutesDefinitions
  }>({})
  displayableRoutes = ref<RoutesDefinitions[]>([])
  showNavigation = ref<boolean>(false)

  currentRouteSettings = ref<RouteDefinition<Routes>>(
    getDefaultTableRoutesOptions() as RouteDefinition<Routes>
  )

  constructor(pejer: PejerEmbeddables, router: Router) {
    this.pejer = pejer
    this.router = router
    this.init()
  }

  init() {
    this.router.isReady().then(() => {
      this.router.afterEach((to, from) => {
        if (history.state[NAVIGATION_REASON] === NAVIGATION_REASON_INDIRECT) return
        this.currentRouteSettings.value =
          history.state[HISTORY_STATE_ROUTE_OBJECT] ?? this.currentRouteSettings.value
        this.handleNavigationChange(to, from, this.currentRouteSettings.value)
      })
    })
  }

  setCurrentRouteSettings(route: Partial<NavigationsObject>) {
    this.currentRouteSettings.value = {
      ...this.currentRouteSettings.value,
      ...route,
      options: {
        ...this.currentRouteSettings.value.options,
        ...route.options,
      } as RouteDefinition['options'],
    } // fillRouteWithDefaults(route)
    history.replaceState(
      {
        ...history.state,
        [HISTORY_STATE_ROUTE_OBJECT]: toRaw(this.currentRouteSettings.value),
      },
      ''
    )
  }

  getRouteSetting<T extends Routes = Routes>(): Ref<RouteDefinition<T>> {
    return this.currentRouteSettings as Ref<RouteDefinition<T>>
  }

  setDisplayNavigationSettings(routeSettings: EmbedMessageSettings['navbar']) {
    // If we don't have any navigation settings, we don't want to change anything
    if (routeSettings === undefined) return

    if (routeSettings == false) {
      this.showNavigation.value = false
      return
    }
    this.showNavigation.value = true

    this.displayableRoutes.value = routeSettings.map((route) => fillRouteWithDefaults(route))
    this.displayableRoutes.value.forEach((route) => {
      this.setRouteSetting(route)
    })
  }

  setRouteSetting(routesObject: RouteDefinition<any>) {
    this.settings[routesObject.path] = fillRouteWithDefaults(routesObject)
  }

  async pushRoute(
    routeObject: NavigationsObject,
    reason:
      | typeof NAVIGATION_REASON_EVENT
      | typeof NAVIGATION_REASON_ROUTER = NAVIGATION_REASON_ROUTER
  ) {
    const path =
      'params' in routeObject
        ? injectUrlWithParams(routeObject.path, routeObject.params)
        : routeObject.path
    await this.router.isReady()
    await this.router.push({
      path,
      state: {
        [NAVIGATION_REASON]: reason,
        [HISTORY_STATE_ROUTE_OBJECT]: toRaw(routeObject) as Record<string, any>,
      },
      force: true, // .!compareObjectsDeep(routeObject, toRaw(this.currentRouteSettings.value)),
    })
  }

  handleNavigationChange(
    to: RouteLocationNormalized,
    from: RouteLocationNormalizedLoaded,
    routeObject: RouteDefinition<any>
  ) {
    this.pejer.postToParent('navigate', {
      from: {
        name: from.name,
        params: from.params,
        fullPath: from.fullPath,
        hash: from.hash,
        path: from.path,
        query: from.query,
      },
      to: {
        name: to.name,
        params: to.params,
        fullPath: to.fullPath,
        hash: to.hash,
        path: to.path,
        query: to.query,
      },
      trigger: history.state[NAVIGATION_REASON] === NAVIGATION_REASON_EVENT ? 'event' : 'router',
      routeName: routeObject.routeName,
    })
  }
}
